import { useEffect, useState } from 'react';

/**
 * RotatingBackground — Voll-Hintergrund aus GREENFORCE-Food-Fotos, die sanft
 * durchcrossfaden (WeTransfer-Prinzip). Sitzt als absolute Ebene im Main-Bereich
 * UNTER der Nav; die zentrierte/linke Sende-Karte floatet darüber.
 *
 * - Interval-Wechsel mit Opacity-Crossfade (kein Layout-Shift, GPU-freundlich).
 * - Lazy: nur das aktive + das nächste Bild bekommen eine background-image-URL →
 *   der Browser lädt nicht alle 8 auf einmal (schlanker First-Paint).
 * - prefers-reduced-motion: KEINE Rotation, statisches erstes Bild.
 * - Dezenter Scrim obendrauf für Lesbarkeit/Card-Pop.
 *
 * Bilder in public/bg/ → /bg/bg-0N.webp von beiden Apps served.
 */
const IMAGES = Array.from(
  { length: 8 },
  (_, i) => `/bg/bg-${String(i + 1).padStart(2, '0')}.webp`,
);
const INTERVAL_MS = 8000;
const FADE_MS = 1500;

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function RotatingBackground() {
  const [reduced] = useState(prefersReducedMotion);
  // Zufälliger Start pro Besuch für Abwechslung; danach sequenziell.
  const [start] = useState(() => Math.floor(Math.random() * IMAGES.length));
  const [step, setStep] = useState(0);
  const active = (start + step) % IMAGES.length;

  // Nur geladene Indizes bekommen eine URL (aktiv + nächstes vorausladen).
  const [loaded, setLoaded] = useState<Set<number>>(
    () => new Set([start, (start + 1) % IMAGES.length]),
  );

  useEffect(() => {
    if (reduced || IMAGES.length <= 1) return;
    const id = setInterval(() => setStep((s) => s + 1), INTERVAL_MS);
    return () => clearInterval(id);
  }, [reduced]);

  useEffect(() => {
    setLoaded((prev) => {
      if (prev.has(active) && prev.has((active + 1) % IMAGES.length)) return prev;
      const next = new Set(prev);
      next.add(active);
      next.add((active + 1) % IMAGES.length);
      return next;
    });
  }, [active]);

  return (
    <div
      className="pointer-events-none absolute inset-0 overflow-hidden bg-brand-green-900"
      aria-hidden="true"
    >
      {IMAGES.map((src, i) => (
        <div
          key={src}
          className="absolute inset-0 bg-cover bg-center transition-opacity ease-in-out"
          style={{
            backgroundImage: loaded.has(i) ? `url('${src}')` : undefined,
            opacity: i === active ? 1 : 0,
            transitionDuration: `${FADE_MS}ms`,
          }}
        />
      ))}
      {/* Lesbarkeits-Scrim: dämpft die Fotos leicht, lässt die weiße Karte poppen. */}
      <div className="absolute inset-0 bg-black/20" />
    </div>
  );
}
